import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Plus, Edit, Trash2, Copy, Package, DollarSign, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCurrentAppUser } from "@/hooks/useCurrentAppUser";
import { appClient } from "@/api/appClient";
import SalesItemModal from "../components/settings/SalesItemModal";

export default function CasesServicesSettings() {
  const navigate = useNavigate();
  const { data: user, isLoading: userLoading } = useCurrentAppUser();
  const [isLoading, setIsLoading] = useState(true);
  const [supabaseConfig, setSupabaseConfig] = useState(null);
  
  // Items state
  const [caseItems, setCaseItems] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  
  // UI state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState("cases");
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, [user, userLoading]);

  useEffect(() => {
    if (supabaseConfig) {
      loadItems();
    }
  }, [supabaseConfig, showInactive, searchQuery, activeTab]);

  const loadInitialData = async () => {
    // Wait for user to load
    if (userLoading) return;
    
    setIsLoading(true);
    try {
      // Check admin access using system_role from app_user
      const isAdmin = user?.system_role?.code === 'admin' || user?.system_role?.name === 'Admin';
      if (!isAdmin) {
        navigate(createPageUrl("Settings"));
        return;
      }

      // Load Supabase config
      console.log("[CasesServicesSettings] Loading Supabase config...");
      const configResponse = await appClient.functions.invoke('getSupabaseConfig', {});
      console.log("[CasesServicesSettings] Config response:", configResponse.data);
      
      if (configResponse.data.success) {
        const config = {
          supabase_url: configResponse.data.supabase_url,
          supabase_anon_key: configResponse.data.supabase_anon_key
        };
        console.log("[CasesServicesSettings] Config loaded:", config);
        setSupabaseConfig(config);
      } else {
        console.error("[CasesServicesSettings] Failed to load Supabase config:", configResponse.data);
        alert("Failed to load configuration. Please check your Supabase settings.");
      }
    } catch (error) {
      console.error("[CasesServicesSettings] Error loading data:", error);
      alert("Error loading configuration: " + error.message);
    }
    setIsLoading(false);
  };

  const loadItems = async () => {
    if (!supabaseConfig) {
      console.log("[CasesServicesSettings] No Supabase config, skipping item load");
      return;
    }

    setIsLoadingItems(true);
    try {
      const { supabase_url, supabase_anon_key } = supabaseConfig;
      console.log("[CasesServicesSettings] Loading items for tab:", activeTab);

      const itemType = activeTab === "cases" ? "case" : "service";
      
      // Build URL with filters
      let url = `${supabase_url}/rest/v1/sales_items?item_type=eq.${itemType}&select=id,name,description,pricing_type,default_price,has_cost,default_cost,is_active,display_order,updated_at,config&order=display_order.asc,name.asc`;
      
      if (!showInactive) {
        url += '&is_active=eq.true';
      }
      
      if (searchQuery) {
        url += `&or=(name.ilike.*${encodeURIComponent(searchQuery)}*,description.ilike.*${encodeURIComponent(searchQuery)}*)`;
      }

      console.log("[CasesServicesSettings] Fetching from:", url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${supabase_anon_key}`,
          'apikey': supabase_anon_key,
          'Content-Type': 'application/json'
        }
      });

      console.log("[CasesServicesSettings] Response status:", response.status);

      if (response.ok) {
        const items = await response.json();
        console.log("[CasesServicesSettings] Loaded items:", items.length);
        
        if (itemType === 'case') {
          setCaseItems(items);
        } else {
          setServiceItems(items);
        }
      } else {
        const errorText = await response.text();
        console.error("[CasesServicesSettings] Failed to load items:", response.status, errorText);
        alert(`Failed to load ${itemType}s: ${response.status} ${response.statusText}`);
      }

    } catch (error) {
      console.error("[CasesServicesSettings] Error loading items:", error);
      alert("Error loading items: " + error.message);
    }
    setIsLoadingItems(false);
  };

  const handleOpenModal = (item = null, itemType = null) => {
    setEditingItem(item);
    if (!item && itemType) {
      setActiveTab(itemType === 'case' ? 'cases' : 'services');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSuccess = () => {
    loadItems();
  };

  const handleSoftDelete = async (item) => {
    if (!supabaseConfig) return;

    try {
      const { supabase_url, supabase_anon_key } = supabaseConfig;

      const response = await fetch(`${supabase_url}/rest/v1/sales_items?id=eq.${item.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${supabase_anon_key}`,
          'apikey': supabase_anon_key,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({ is_active: false })
      });

      if (response.ok) {
        loadItems();
        setDeleteConfirm(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("[CasesServicesSettings] Failed to deactivate item:", errorData);
        alert("Failed to deactivate item");
      }
    } catch (error) {
      console.error("[CasesServicesSettings] Error deactivating:", error);
      alert("Error: " + error.message);
    }
  };

  const handleDuplicate = async (item) => {
    if (!supabaseConfig) return;

    try {
      const { supabase_url, supabase_anon_key } = supabaseConfig;

      const payload = {
        item_type: item.item_type,
        name: `${item.name} (copy)`,
        description: item.description,
        pricing_type: item.pricing_type,
        default_price: item.default_price,
        has_cost: item.has_cost,
        default_cost: item.default_cost,
        is_active: true,
        display_order: (item.display_order || 0) + 1,
        config: item.config || {}
      };

      const response = await fetch(`${supabase_url}/rest/v1/sales_items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabase_anon_key}`,
          'apikey': supabase_anon_key,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        loadItems();
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          alert("An item with this name already exists. Please rename the duplicate.");
        } else {
          console.error("[CasesServicesSettings] Failed to duplicate item:", errorData);
          alert("Failed to duplicate item");
        }
      }
    } catch (error) {
      console.error("[CasesServicesSettings] Error duplicating:", error);
      alert("Error: " + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const renderItemCard = (item) => (
    <div
      key={item.id}
      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
    >
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="font-semibold text-slate-900">{item.name}</h3>
          {!item.is_active && (
            <Badge variant="outline" className="bg-slate-100 text-slate-500">
              Inativo
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            {item.pricing_type === 'flat' ? 'Preço Fixo' : 'Variável'}
          </Badge>
          {item.config?.tags && item.config.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.config.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {item.description && (
          <p className="text-sm text-slate-600 mb-2">{item.description}</p>
        )}
        <div className="flex gap-4 text-sm">
          <span className="text-slate-600">
            Preço: <span className="font-semibold text-slate-900">{formatCurrency(item.default_price)}</span>
          </span>
          {item.has_cost && (
            <span className="text-slate-600">
              Custo: <span className="font-semibold text-slate-900">{formatCurrency(item.default_cost)}</span>
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleOpenModal(item)}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDuplicate(item)}
          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDeleteConfirm(item)}
          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!supabaseConfig) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load Supabase configuration. Please check your environment variables.
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate(createPageUrl("Settings"))} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Settings
          </Button>
        </div>
      </div>
    );
  }

  const currentFixedItemType = activeTab === "cases" ? "case" : "service";
  const currentItems = activeTab === "cases" ? caseItems : serviceItems;

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Configurações
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Processos & Serviços</h1>
          <p className="text-slate-600">Gerencie os tipos de processos e serviços oferecidos</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-slate-200">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label>Mostrar inativos</Label>
          </div>
          <Button onClick={loadItems} variant="outline" disabled={isLoadingItems}>
            Atualizar
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="cases" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Tipos de Processo
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Serviços
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cases" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">Tipos de Processo</CardTitle>
                    <CardDescription>Defina os tipos de processos que o escritório atende</CardDescription>
                  </div>
                  <Button
                    onClick={() => handleOpenModal(null, 'case')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Tipo de Processo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {isLoadingItems ? (
                  <div className="text-center py-12 text-slate-500">Carregando...</div>
                ) : currentItems.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    Nenhum tipo de processo ainda. Clique em "Adicionar Tipo de Processo" para criar.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentItems.map(renderItemCard)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">Tipos de Serviço</CardTitle>
                    <CardDescription>Defina os serviços oferecidos pelo escritório</CardDescription>
                  </div>
                  <Button
                    onClick={() => handleOpenModal(null, 'service')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Serviço
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {isLoadingItems ? (
                  <div className="text-center py-12 text-slate-500">Carregando...</div>
                ) : currentItems.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    Nenhum serviço ainda. Clique em "Adicionar Serviço" para criar.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentItems.map(renderItemCard)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Sales Item Modal */}
        <SalesItemModal
          isOpen={showModal}
          onClose={handleCloseModal}
          editingItem={editingItem}
          fixedItemType={currentFixedItemType}
          supabaseConfig={supabaseConfig}
          onSuccess={handleSuccess}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Desativar {deleteConfirm?.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Isso marcará o item como inativo. Ele não será excluído e pode ser reativado depois.
                Habilite "Mostrar inativos" para ver e gerenciar itens inativos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleSoftDelete(deleteConfirm)}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Desativar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}